import { useI18n } from '#imports'
import { useSite } from '@/composables/core/useSite'
import { getLocaleUrl, getLocaleText, filterActivityLocaleSetting, shouldFilterLocaleSetting } from '@/utils/localeSetting'

/**
 * 語系設定 Composable
 * 提供簡潔的 API 來取得語系化資料
 * @returns {Object} 包含 getUrl、getText、get、getFromActivity、shouldFilter 方法
 *
 * 使用規則：
 * - getFromActivity(activity, key)：傳入含有 activityLocaleSettings 或 localeSetting 的 activity 物件
 *   適用於 activityList 中的項目、props.activity 等
 *   內部依下列順序判斷資料來源：
 *     1. activityLocaleSettings 為非空陣列 → 使用 activityLocaleSettings 過濾（key 'title' 自動轉為 'activityName'）
 *     2. localeSetting 為非空陣列 → 使用 localeSetting 過濾
 *     3. 兩者皆為空陣列 → 直接回傳 activity 上對應 key 的值
 *
 * - getUrl(localeSetting, key) / getText(localeSetting, key)：傳入 localeSetting 陣列
 *   適用於 activityDetail、promotion 等需手動指定陣列的場景
 *
 * @example
 * const { getUrl, getText, getFromActivity } = useLocaleSetting()
 *
 * // activity 物件（含 template）→ 使用 getFromActivity
 * const imageUrl = getFromActivity(activity, 'adUrl')
 * const title = getFromActivity(activity, 'title')
 *
 * // activityDetail（無 template）→ 使用 getUrl / getText + 指定陣列
 * const iconUrl = getUrl(activityDetail.activityLocaleSettings, 'friendLevel1Url')
 * const descrip = getText(activityDetail.activityLocaleSettings, 'description')
 *
 * // promotion（無 template）→ 使用 getUrl / getText + 指定陣列
 * const promoTitle = getText(promotion.localeSetting, 'title')
 */
export function useLocaleSetting() {
  const { locale } = useI18n()
  const { locale: siteLocale } = useSite()

  /**
   * 取得 URL 資料（從 localeSetting 陣列）
   * @param {Array} localeSetting - 語系設定陣列
   * @param {string} key - 資料鍵名
   * @returns {string|Array} key 為 'descriptionUrl' 時回傳陣列，其餘回傳 url 字串
   */
  const getUrl = (localeSetting, key) => {
    return getLocaleUrl(localeSetting, locale.value, siteLocale.value, key)
  }

  /**
   * 取得文字資料（從 localeSetting 陣列）
   * @param {Array} localeSetting - 語系設定陣列
   * @param {string} key - 資料鍵名
   * @returns {string} 文字內容
   */
  const getText = (localeSetting, key) => {
    return getLocaleText(localeSetting, locale.value, siteLocale.value, key)
  }

  /**
   * 自動判斷取得 URL 或文字資料（從 localeSetting 陣列）
   * @param {Array} localeSetting - 語系設定陣列
   * @param {string} key - 資料鍵名
   * @returns {string|Array} url 或文字內容
   */
  const get = (localeSetting, key) => {
    const isUrl = String(key ?? '')
      .toUpperCase()
      .includes('URL')
    return isUrl ? getUrl(localeSetting, key) : getText(localeSetting, key)
  }

  /**
   * 從 activity/promotion 物件取得語系化資料
   * 自動判斷使用 activityLocaleSettings 或 localeSetting
   * @param {Object} activity - 活動或優惠物件
   * @param {string} key - 資料鍵名
   * @returns {string|Array} url 或文字內容
   */
  const getFromActivity = (activity, key) => {
    return filterActivityLocaleSetting(activity, locale.value, siteLocale.value, key)
  }

  /**
   * 檢查是否需要過濾 localeSetting（是否為特定 activity 類型）
   * @param {Object} activity - 活動物件
   * @returns {boolean} 是否需要過濾
   */
  const shouldFilter = (activity) => {
    return shouldFilterLocaleSetting(activity)
  }

  return {
    getUrl,
    getText,
    get,
    getFromActivity,
    shouldFilter,
  }
}
